import React, { useEffect, useCallback, useMemo } from 'react';
import { Form, Input, Button, message } from 'antd';
import type { InputRef } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { resetPagination } from 'core/dashboard/dashboardSlice';
import { resetPagination as resetSecurityPagination } from 'core/securityDashboard/securityDashboardSlice';
import { twoFactorConfirmation, twoFactorResend } from '../../../core/user/userApi';
import { FinishedFailedProps, valuesProps } from './types';
import { AppDispatch, CombinedStates } from '../../../core/types';
import './twoFactorAuth.scss';
import { SECURITY_USER } from '../../../core/utils/value';

const CODE_LENGTH = 4;

const TwoFactorAuthForm = () => {
    const inputRefs = useMemo(() => Array.from({ length: CODE_LENGTH }, () => React.createRef<InputRef>()), []);
    const navigation = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const {
        isFetching,
        isLoggedIn,
        userData,
        showTwoFactorPage,
        rateLimit,
    } = useSelector((state:CombinedStates) => state.user);
    const [form] = Form.useForm();

    const focusInput = useCallback((index: number) => {
        const ref = inputRefs[index];
        if (ref?.current) {
            ref.current.focus();
        }
    }, [inputRefs]);

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
        const target = e.target as HTMLInputElement;
        const { key } = e;

        // Enter submits the whole code from any digit box.
        if (key === 'Enter') {
            e.preventDefault();
            form.submit();
            return;
        }

        // Let keyboard shortcuts through (Ctrl/Cmd + V to paste, plus C/A/X
        // etc.) — otherwise the digit-only guard below cancels the "v" keydown
        // and blocks Ctrl+V, even though right-click paste still works.
        if (e.ctrlKey || e.metaKey) {
            return;
        }

        // Handle backspace
        if (key === 'Backspace') {
            if (!target.value && index > 0) {
                e.preventDefault();
                form.setFieldValue(`code${index - 1}`, '');
                focusInput(index - 1);
            }
        } else if (key === 'ArrowLeft' && index > 0) {
            e.preventDefault();
            focusInput(index - 1);
        } else if (key === 'ArrowRight' && index < CODE_LENGTH - 1) {
            e.preventDefault();
            focusInput(index + 1);
        } else if (!/^\d$/.test(key) && !['Tab', 'Delete', 'Home', 'End'].includes(key)) {
            e.preventDefault();
        }
    }, [form, focusInput]);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>, index: number) => {
        const { value } = e.target;

        // Only accept single digit
        if (value && /^\d$/.test(value)) {
            if (index < CODE_LENGTH - 1) {
                focusInput(index + 1);
            }
        }
    }, [focusInput]);

    // Let the user paste the whole code into any box — the digits are spread
    // across the boxes from where the paste happened (usually the first).
    const handlePaste = useCallback((e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text');
        const digits = pasted.replace(/\D/g, '');
        if (!digits) {
            // Pasted something, but it had no digits — say why nothing filled.
            if (pasted.trim() !== '') {
                message.error('The verification code must be numbers only.');
            }
            return;
        }

        // A full-length code fills from the first box wherever it's pasted; a
        // partial paste fills from the box the user pasted into. Collect all
        // the box values and apply them in one setFieldsValue call (single
        // re-render) rather than one setFieldValue per digit.
        const start = digits.length >= CODE_LENGTH ? 0 : index;
        const updates: Record<string, string> = {};
        let lastIndex = start;
        digits.split('').forEach((digit, offset) => {
            const targetIndex = start + offset;
            if (targetIndex < CODE_LENGTH) {
                updates[`code${targetIndex}`] = digit;
                lastIndex = targetIndex;
            }
        });
        form.setFieldsValue(updates);
        focusInput(lastIndex);
    }, [form, focusInput]);

    const onFinish = useCallback((values: valuesProps) => {
        const code = Object.keys(values)
            .sort()
            .reduce((str, key) => str + (values[key as keyof valuesProps] || ''), '');

        if (code.length === CODE_LENGTH) {
            dispatch(twoFactorConfirmation({
                code,
                callback: () => {
                    dispatch(resetPagination());
                    dispatch(resetSecurityPagination());
                },
            }));
        }
    }, [dispatch]);

    const onFinishFailed = useCallback(({ errorFields }: FinishedFailedProps) => {
        if (errorFields?.[0]?.name) {
            form.scrollToField(errorFields[0].name);
        }
    }, [form]);

    const handleResendCode = useCallback(() => {
        dispatch(twoFactorResend());
        // Clear any already-entered digits and refocus the first box so the
        // user starts fresh with the newly sent code.
        form.resetFields();
        focusInput(0);
    }, [dispatch, form, focusInput]);

    useEffect(() => {
        focusInput(0);
    }, [focusInput]);

    // After too many incorrect codes the pre-2FA token is spent (cleared by
    // the thunk and revoked server-side), so send the user back to login to
    // re-authenticate from scratch.
    useEffect(() => {
        if (rateLimit) {
            navigation('/');
        }
    }, [rateLimit, navigation]);

    useEffect(() => {
        const { role } = userData;
        const accessRole = role?.accessLevel || null;

        if (isLoggedIn && accessRole === SECURITY_USER) {
            navigation('/security-dashboard');
        } else if (isLoggedIn) {
            navigation('/dashboard');
        } else if (showTwoFactorPage) {
            navigation('/two-factor');
        }
    }, [isLoggedIn, showTwoFactorPage, userData, navigation]);

    return (
        <div className="two-factor-form-box">
            <div className="form">
                <h3 className="title-text">Verification Code</h3>
                <div className="form-sub-title">
                    Please enter the verification code we sent to your email address
                </div>
                <div className="form-sub-title2">
                    Enter Code
                </div>
                <Form
                    form={form}
                    onFinish={onFinish}
                    onFinishFailed={onFinishFailed}
                    layout="horizontal">
                    {inputRefs.map((ref, index) => (
                        <Form.Item
                            name={`code${index}`}
                            // eslint-disable-next-line react/no-array-index-key
                            key={`verification-code-${index}`}
                            rules={[{ required: true, message: '' }]}>
                            <Input
                                ref={ref}
                                maxLength={1}
                                inputMode="numeric"
                                pattern="[0-9]"
                                autoComplete="one-time-code"
                                aria-label={`Verification code digit ${index + 1}`}
                                onKeyDown={(e) => handleKeyDown(e, index)}
                                onPaste={(e) => handlePaste(e, index)}
                                onChange={(e) => handleChange(e, index)} />
                        </Form.Item>
                    ))}

                    <Form.Item>
                        <Button
                            type="primary"
                            size="large"
                            htmlType="submit"
                            style={{ width: '300px' }}
                            className="mt-3"
                            loading={isFetching}
                            disabled={isFetching}>
                            Submit
                        </Button>
                    </Form.Item>
                </Form>
                <div className="text-center">
                    <Button
                        onClick={handleResendCode}
                        type="link"
                        className="button-link"
                        disabled={isFetching}>
                        <span>Resend Code</span>
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default TwoFactorAuthForm;
